<script>
import axios from 'axios'
import { mapGetters } from 'vuex'

import { SupplierRoutes } from '../api/supplierRoutes'

export default {
  name: 'ShopifyReauthModal',

  data: () => ({
    needsReauth: false,
    // Dismissed only for the current session. Visibility is derived from the
    // server check, so the modal reappears on every app load until the store
    // is re-authenticated.
    dismissed: false,
    checked: false,
  }),

  computed: {
    ...mapGetters({
      user: 'auth/user',
    }),

    // getUserData loads the `store` HasMany relation, so it arrives as an array.
    // Suppliers have a single connected store.
    store() {
      const stores = this.user && this.user.store
      if (Array.isArray(stores)) {
        return stores[0] || null
      }
      return stores || null
    },

    isOpen() {
      return this.needsReauth && !this.dismissed
    },
  },

  watch: {
    // The user is fetched asynchronously, so the store may not be present when
    // this component is created. Run the check once, as soon as a store exists.
    store: {
      immediate: true,
      handler(store) {
        if (store && !this.checked) {
          this.checked = true
          this.checkReauth()
        }
      },
    },
  },

  methods: {
    async checkReauth() {
      try {
        const { data } = await axios.get(SupplierRoutes.shopifyReauthStatus)
        this.needsReauth = data && data.needs_reauth === true
      } catch (e) {
        this.needsReauth = false
      }
    },

    async authorize() {
      // The server builds the Shopify OAuth URL directly (bypassing the
      // already-connected guard that the normal connect entry enforces), so
      // reauthorizing an already-connected store works. Shopify redirects back
      // to the supplier dashboard once the app is re-approved.
      try {
        const { data } = await axios.get(SupplierRoutes.shopifyReauthUrl)
        if (data && data.url) {
          window.location = data.url
        }
      } finally {
        this.dismissed = true
      }
    },
  },
}
</script>

<template>
  <div
    v-if="isOpen"
    class="shopify-reauth-modal"
  >
    <div class="shopify-reauth-modal-backdrop" />
    <div
      class="shopify-reauth-modal-dialog"
      role="dialog"
      aria-modal="true"
    >
      <h3 class="shopify-reauth-modal-title">
        Shopify authentication required
      </h3>
      <p class="shopify-reauth-modal-text">
        Your Shopify store connection has expired. Please re-authenticate to keep your store connected.
      </p>
      <div class="shopify-reauth-modal-actions">
        <button
          type="button"
          class="btn btn-success"
          @click="authorize"
        >
          Authorize on Shopify
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shopify-reauth-modal-backdrop {
  position: fixed;
  z-index: 1050;
  inset: 0;
  background: rgb(0 0 0 / 50%);
}

.shopify-reauth-modal-dialog {
  position: fixed;
  z-index: 1051;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 480px;
  padding: 32px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 10px 40px rgb(0 0 0 / 20%);
  text-align: center;
}

.shopify-reauth-modal-title {
  margin: 0 0 12px;
  font-weight: 600;
}

.shopify-reauth-modal-text {
  margin: 0 0 24px;
  color: #555;
}

.shopify-reauth-modal-actions {
  display: flex;
  justify-content: center;
}
</style>
